import clsx from 'clsx';
import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

interface FieldWrapperProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

/**
 * Label, control, hint and error in one consistent block.
 *
 * The error is wired to the control with `aria-describedby` by the callers
 * below, so assistive technology announces why a field was rejected rather than
 * only showing red text.
 */
export function FieldWrapper({ label, htmlFor, error, hint, required, children }: FieldWrapperProps) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-medium text-muted">
        {label}
        {required === true && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {children}
      {error !== undefined ? (
        <p id={`${htmlFor}-error`} className="animate-fade-in mt-1.5 text-xs text-danger">
          {error}
        </p>
      ) : (
        hint !== undefined && (
          <p id={`${htmlFor}-hint`} className="mt-1.5 text-xs text-subtle">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

const CONTROL =
  'block w-full rounded-lg border border-line bg-card px-3 py-2 text-sm text-ink shadow-xs ' +
  'placeholder:text-subtle transition-[border-color,box-shadow] duration-150 ' +
  'hover:border-line-strong ' +
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)] ' +
  'disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-subtle';

const CONTROL_ERROR = 'border-danger focus:border-danger focus:ring-danger/30';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, id, className, required, ...rest },
  ref,
) {
  const fieldId = id ?? rest.name ?? label;
  return (
    <FieldWrapper label={label} htmlFor={fieldId} error={error} hint={hint} required={required}>
      <input
        id={fieldId}
        ref={ref}
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? `${fieldId}-error` : undefined}
        className={clsx(CONTROL, error !== undefined && CONTROL_ERROR, className)}
        {...rest}
      />
    </FieldWrapper>
  );
});

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, id, className, required, children, ...rest },
  ref,
) {
  const fieldId = id ?? rest.name ?? label;
  return (
    <FieldWrapper label={label} htmlFor={fieldId} error={error} hint={hint} required={required}>
      <select
        id={fieldId}
        ref={ref}
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? `${fieldId}-error` : undefined}
        className={clsx(CONTROL, 'appearance-none bg-no-repeat pr-9', error !== undefined && CONTROL_ERROR, className)}
        style={{
          // Inline so the chevron can follow the theme token; a Tailwind
          // arbitrary value cannot interpolate a CSS variable into a data URI.
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2364748b'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E\")",
          backgroundPosition: 'right 0.625rem center',
          backgroundSize: '1.15rem',
        }}
        {...rest}
      >
        {children}
      </select>
    </FieldWrapper>
  );
});
